import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

/**
 * Drop-in <input> replacement with a live suggestion dropdown.
 *
 * Props (beyond standard input):
 *   suggestions  – string[]  – full list of candidates (filtered client-side)
 *   onSelect     – fn(value) – called after the user picks a suggestion (for cross-field fill)
 */
const AutocompleteInput = ({
  suggestions = [],
  onSelect,
  value = '',
  onChange,
  name,
  className = 'form-input',
  ...rest
}) => {
  const [open, setOpen]             = useState(false);
  const [filtered, setFiltered]     = useState([]);
  const [activeIdx, setActiveIdx]   = useState(-1);
  const [dropStyle, setDropStyle]   = useState({});
  const inputRef   = useRef(null);
  const dropRef    = useRef(null);

  /* ── filter list whenever value or suggestions change ── */
  useEffect(() => {
    const q = String(value || '').toLowerCase().trim();
    const result = q === ''
      ? suggestions.slice(0, 9)
      : suggestions.filter(s => s.toLowerCase().includes(q)).slice(0, 9);
    setFiltered(result);
    setActiveIdx(-1);
  }, [value, suggestions]);

  /* ── position dropdown via fixed coords (escapes all overflow containers) ── */
  const reposition = useCallback(() => {
    if (!inputRef.current) return;
    const r = inputRef.current.getBoundingClientRect();
    setDropStyle({
      position: 'fixed',
      top:      r.bottom + 3,
      left:     r.left,
      width:    r.width,
      zIndex:   99999,
    });
  }, []);

  /* ── select a suggestion ── */
  const pick = useCallback((val) => {
    onChange({ target: { name, value: val } });
    if (onSelect) onSelect(val);
    setOpen(false);
    setActiveIdx(-1);
    inputRef.current?.blur();
  }, [name, onChange, onSelect]);

  /* ── input events ── */
  const handleChange = (e) => { onChange(e); reposition(); setOpen(true); };
  const handleFocus  = ()  => { reposition(); if (filtered.length > 0) setOpen(true); };
  const handleBlur   = ()  => { setTimeout(() => setOpen(false), 150); };

  const handleKeyDown = (e) => {
    if (!open || filtered.length === 0) {
      if (e.key === 'ArrowDown' && filtered.length > 0) { reposition(); setOpen(true); }
      return;
    }
    if (e.key === 'ArrowDown')  { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); pick(filtered[activeIdx]); }
    else if (e.key === 'Escape')    { setOpen(false); }
  };

  /* ── scroll highlighted item into view ── */
  useEffect(() => {
    if (activeIdx >= 0 && dropRef.current) {
      dropRef.current.querySelectorAll('.ac-item')[activeIdx]?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIdx]);

  const show = open && filtered.length > 0;

  return (
    <>
      <input
        ref={inputRef}
        name={name}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={className}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        {...rest}
      />
      {show && ReactDOM.createPortal(
        <div ref={dropRef} className="ac-dropdown" style={dropStyle}>
          {filtered.map((s, i) => (
            <div
              key={i}
              className={`ac-item${i === activeIdx ? ' ac-item-active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
            >
              {s}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

export default AutocompleteInput;
